import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { usePopper } from 'react-popper'
import { UploadIcon, CheckCheckIcon, CheckIcon } from 'images'
import { buildMediaSlots, computeAspectRatio, fileStatus } from 'components/Recommendations/Campaign/Media/media'
import ActionMenu from 'components/Shared/MediaCard/ActionMenu'
import LoaderSpinner from 'components/Shared/Loader/LoaderSpinner'
import { LibraryPickerModal } from 'components/Library/element/LibraryPickerModal'
import { useMediaGroupSlotActions } from '../PreviewModal/useMediaGroupSlotActions'
import { useTranslation } from 'react-i18next'

const POPPER_OPTIONS = {
  placement: 'bottom-start',
  strategy: 'fixed',
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    { name: 'preventOverflow', options: { padding: 8 } },
  ],
}

const getSlotMenuItems = ({
  image,
  t,
  readonly,
  onLibraryAdd,
  onDeviceAdd,
  onLibraryReplace,
  onDeviceReplace,
  onRemove,
}) => {
  if (readonly) {
    return []
  }

  if (!image) {
    return [
      {
        key: 'library',
        label: t('Recommendations.campaign.setup.media.dropdown.add_from_library'),
        onClick: onLibraryAdd,
      },
      {
        key: 'device',
        label: t('Recommendations.campaign.setup.media.dropdown.add_from_device'),
        onClick: onDeviceAdd,
      },
    ]
  }

  const items = [
    {
      key: 'library',
      label: t('Recommendations.campaign.setup.media.dropdown.replace_from_library'),
      onClick: onLibraryReplace,
    },
    {
      key: 'device',
      label: t('Recommendations.campaign.setup.media.dropdown.replace_from_device'),
      onClick: onDeviceReplace,
    },
  ]

  if (onRemove) {
    items.push({
      key: 'remove',
      label: t('Recommendations.campaign.setup.media.dropdown.remove'),
      onClick: onRemove,
    })
  }

  return items
}

const getSlotIcon = ({ isEmpty, isUploading, isGenerating, isReady }) => {
  if (isUploading || (isEmpty && isGenerating && !isReady)) {
    return <LoaderSpinner size="xs" />
  }

  if (isEmpty && isGenerating && isReady) {
    return <CheckIcon width={14} height={14} className="media-list-card__slot-check" />
  }

  if (isEmpty) {
    return <UploadIcon width={14} height={14} />
  }

  return <CheckCheckIcon width={14} height={14} />
}

const SlotButton = ({ image, ratio, isMenuOpen, onToggle, onClose, menuItems, isReady, isGenerating, readonly, hasError = false }) => {
  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, POPPER_OPTIONS)

  const isEmpty = !image
  const isDisabled = readonly || isGenerating
  const isUploading = image?.status === fileStatus.new || image?.status === fileStatus.uploading
  const itemType = image?.is_generated ? 'generated' : 'uploaded'
  // Filled slot: show actual image ratio (e.g. 4:5 in the 1:1 feed slot). Empty: show slot ratio.
  // 9:16 is always shown as the slot ratio itself, never recomputed — it's
  // Meta's Reels placement (and, for Google Display, exists only to cover
  // that placement), so there's no finer "real" ratio worth surfacing there.
  const displayRatio = image
    ? (ratio === '9:16'
      ? ratio
      : ((image.width && image.height
        ? computeAspectRatio(image.width, image.height)
        : null) ?? image.ratio ?? ratio))
    : ratio
  const buttonClassName = [
    'media-list-card__item-button',
    isEmpty ? 'empty' : itemType,
    isUploading ? 'uploading' : '',
    isEmpty && hasError ? 'has-error' : '',
  ].filter(Boolean).join(' ')

  useEffect(() => {
    if (isUploading && isMenuOpen) {
      onClose()
    }
  }, [isUploading, isMenuOpen, onClose])

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (referenceElement?.contains(event.target) || popperElement?.contains(event.target)) {
        return
      }

      onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen, referenceElement, popperElement, onClose])

  return (
    <div className="media-list-card__slot-button-wrapper">
      <button
        ref={setReferenceElement}
        className={buttonClassName}
        type="button"
        disabled={isUploading || isDisabled}
        style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
        onClick={(event) => {
          event.stopPropagation()
          if (isUploading || isDisabled) {
            return
          }
          onToggle()
        }}
      >
        {getSlotIcon({ isEmpty, isUploading, isGenerating, isReady })}

        <span className="ratio">{displayRatio}</span>
      </button>


      {isMenuOpen && !isUploading && menuItems.length > 0 && createPortal(
        <div
          ref={setPopperElement}
          className="media-action-menu-popper"
          style={styles.popper}
          {...attributes?.popper}
        >
          <ActionMenu
            actionMenuItems={menuItems}
            setIsMenuOpen={onClose}
          />
        </div>,
        document.body,
      )}
    </div>
  )
}

const CardContent = ({
  isUploading,
  isGenerating,
  isReady,
  subGroupItems,
  subGroupId,
  originalId,
  originalMedia,
  isCampaignRunning = false,
  readonly = false,
  onRemove,
  isError,
  onAddToSlot,
  onAddLibraryToSlot,
  onReplaceLibraryToSlot,
  onReplaceInSlot,
  onRemoveFromSlot,
  setSlotError,
  requiredSlotRatios,
  isGoogleOnly = false,
  isGoogleSelected = false,
  googleExactRatios = [],
  showValidationErrors = false,
  missingGoogleExactRatios = [],
}) => {
  const { t } = useTranslation()
  const images = subGroupItems ?? []
  const mediaSlots = buildMediaSlots(images, requiredSlotRatios)

  const isVideoGroup = images.some((item) => item.type?.startsWith('video/'))
  const [openMenuRatio, setOpenMenuRatio] = useState(null)
  const closeMenu = useCallback(() => setOpenMenuRatio(null), [])

  const group = {
    subGroupId,
    originalId,
    images,
  }

  const {
    isLibraryPickerOpen,
    fileInputRef,
    handleFileChange,
    openFilePicker,
    openLibraryAdd,
    openLibraryReplace,
    closeLibraryPicker,
    handleLibraryApply,
    handleRemoveSlot,
  } = useMediaGroupSlotActions({
    group,
    images,
    originalMedia,
    canManageMedia: !readonly,
    onAddToSlot,
    onAddLibraryToSlot,
    onReplaceLibraryToSlot,
    onReplaceInSlot,
    onRemoveFromSlot,
    setSlotError,
    requiredSlotRatios,
    isGoogleOnly,
    isGoogleSelected,
    googleExactRatios,
  })

  return (
    <div className="media-list-card__content" style={{ pointerEvents: isUploading || isGenerating ? 'none' : 'auto', opacity: isGenerating ? 0.7 : 1 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png, video/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {isError && (
        <p className="error-message" onClick={onRemove ? () => onRemove() : undefined}>
          {t('Recommendations.campaign.setup.media.removeMedia')}
        </p>
      )}

      {!isError && <div className="media-list-card__buttons-container">
        {mediaSlots.map(({ ratio, image }) => {
          const canRemoveImage = Boolean(image) && !readonly && (!isCampaignRunning || images.length > 1)

          const menuItems = getSlotMenuItems({
            image,
            t,
            readonly,
            onLibraryAdd: () => openLibraryAdd(ratio),
            onDeviceAdd: () => openFilePicker({ type: 'add', ratio }),
            onLibraryReplace: image
              ? () => openLibraryReplace(ratio, image.id)
              : undefined,
            onDeviceReplace: image
              ? () => openFilePicker({ type: 'replace', fileId: image.id, ratio })
              : undefined,
            onRemove: canRemoveImage
              ? () => handleRemoveSlot(image.id)
              : undefined,
          })

          // Never shown for a video-anchor group (see isVideoGroup above) — Google can't use video at all.
          const hasError = !isVideoGroup && isGoogleSelected && showValidationErrors && missingGoogleExactRatios.includes(ratio)

          return (
            <SlotButton
              key={ratio}
              image={image}
              ratio={ratio}
              isMenuOpen={openMenuRatio === ratio}
              menuItems={menuItems}
              onToggle={() => {
                if (readonly || menuItems.length === 0) {
                  return
                }

                setOpenMenuRatio((current) => (current === ratio ? null : ratio))
              }}
              onClose={closeMenu}
              isReady={isReady}
              isGenerating={isGenerating}
              readonly={readonly}
              hasError={hasError}
            />
          )
        })}
      </div>}

      <LibraryPickerModal
        isOpen={isLibraryPickerOpen}
        onClose={closeLibraryPicker}
        onApply={handleLibraryApply}
        selectionMode="single"
      />
    </div>
  )
}

CardContent.propTypes = {
  subGroupItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  subGroupId: PropTypes.string,
  originalId: PropTypes.string,
  originalMedia: PropTypes.object,
  isCampaignRunning: PropTypes.bool,
  readonly: PropTypes.bool,
  onRemove: PropTypes.func,
  isError: PropTypes.bool,
  onAddToSlot: PropTypes.func,
  onAddLibraryToSlot: PropTypes.func,
  onReplaceLibraryToSlot: PropTypes.func,
  onReplaceInSlot: PropTypes.func,
  onRemoveFromSlot: PropTypes.func,
  setSlotError: PropTypes.func,
  requiredSlotRatios: PropTypes.arrayOf(PropTypes.string),
  isGoogleOnly: PropTypes.bool,
  isGoogleSelected: PropTypes.bool,
  googleExactRatios: PropTypes.arrayOf(PropTypes.string),
  showValidationErrors: PropTypes.bool,
  missingGoogleExactRatios: PropTypes.arrayOf(PropTypes.string),
}

SlotButton.propTypes = {
  image: PropTypes.object,
  ratio: PropTypes.string.isRequired,
  isMenuOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })).isRequired,
  isReady: PropTypes.bool,
  isGenerating: PropTypes.bool,
  readonly: PropTypes.bool,
  hasError: PropTypes.bool,
}

export default CardContent
